/**
 * LandingOnboardingPreview — New workspace / onboarding screen for Workspaces section.
 * Shows: "Tell me what to do today to not lose money" + system note + 3 setup steps.
 * Presentational only (no navigation); demo safe.
 */

import * as React from "react";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = [
  { number: 1, title: "Import your leads", description: "Upload a CSV or add leads manually", startHere: true, hint: "Without leads, Today can't decide." },
  { number: 2, title: "Set your monthly target", description: "Define your revenue goal" },
  { number: 3, title: "Connect a lead source", description: "Connect Meta, webhooks, or other sources" },
] as const;

export function LandingOnboardingPreview() {
  return (
    <div className="flex h-full w-full flex-col bg-[#0b0b0f] p-5 text-left dark">
      {/* Top bar / context (optional minimal) */}
      <div className="mb-4 flex items-center gap-2 text-xs text-white/40">
        <span>Today</span>
        <span>·</span>
        <span>New workspace</span>
      </div>

      {/* Title — matches Today page */}
      <h1 className="text-lg font-semibold text-white mb-1">
        Tell me what to do today to not lose money
      </h1>

      {/* System note */}
      <div className="mt-3 pl-4 border-l-2 border-white/20 py-1 mb-6">
        <p className="text-xs text-white/70 leading-relaxed">
          <span className="font-medium text-white/90">System note:</span> Decision Sales OS does not ask what you want to do — it tells you what you must do to make money.
        </p>
      </div>

      {/* To start, bring leads in */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-white mb-0.5">To start, bring leads in.</h2>
        <p className="text-xs text-white/50 leading-relaxed">
          Import a CSV or connect Meta so Today can decide what to do.
        </p>
        <button
          type="button"
          className="text-xs font-medium text-primary mt-1.5 opacity-90"
          style={{ pointerEvents: "none" }}
        >
          How DSOS works
        </button>
      </div>

      {/* 3 setup steps */}
      <div className="flex flex-col gap-4">
        {STEPS.map((s) => (
          <div key={s.number} className="flex flex-col gap-2">
            {s.startHere && (
              <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary w-fit">
                Start here
              </span>
            )}
            <div
              className={cn(
                "flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5"
              )}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-sm font-semibold text-primary">
                {s.number}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">{s.title}</p>
                <p className="text-xs text-white/50 mt-0.5">{s.description}</p>
                {s.hint && (
                  <p className="text-[11px] text-white/40 mt-1">{s.hint}</p>
                )}
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-white/30" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
